import { makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { AxiosError } from 'axios'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { type IDropdownItem } from 'shared/ui'
import { logger, numberFormat } from 'shared/lib'
import { ZIndex } from 'shared/constants/zIndex'
import { BillingApi } from 'entities/Billing/api/billing'
import { type IParamsAutorecharge } from 'entities/Billing/api/types'
import { type IAutoRechargeWidgetStoreOptions } from 'entities/Billing/ui/AutoRechargeModal/store/types'
import { DropdownCardsStore } from 'entities/Billing/ui/DropdownCards'
import { AutoRechargeModalAction } from 'entities/Billing/ui/AutoRechargeModal/ui/AutoRechargeModalContent/AutoRechargeModalAction'
import { AutoRechargeModalContent } from 'entities/Billing/ui/AutoRechargeModal/ui/AutoRechargeModalContent/AutoRechargeModalContent'
import type { BillingStore } from 'entities/Billing'
import { type SubscriptionStore } from 'entities/Subscription'

export class AutoRechargeWidgetStore {
  private _options: IAutoRechargeWidgetStoreOptions | null = null
  private _modalId = ''
  private _confirmDisableModalId = ''
  private _primaryDropdownCardsStore: DropdownCardsStore
  private _threshold_in_credits = 100
  private _amount_in_credits = 1000
  private _amountItems: IDropdownItem[] = []
  private _error: string | null = null

  constructor(
    private _billingStore: BillingStore,
    private _subscriptionStore: SubscriptionStore,
    options?: IAutoRechargeWidgetStoreOptions
  ) {
    this._options = options || null
    this._primaryDropdownCardsStore = new DropdownCardsStore(
      this._billingStore,
      this._subscriptionStore,
      {
        labelText: 'Payment method',
      }
    )
    this._threshold_in_credits = this._billingStore.autorecharge?.threshold_in_credits || 100
    this._amount_in_credits = this._billingStore.autorecharge?.amount_in_credits || 1000

    makeAutoObservable(this)
  }

  private get params(): IParamsAutorecharge {
    return {
      source: this._primaryDropdownCardsStore.activeId || null,
      backup: null,
      amount: this._amount_in_credits,
      threshold: this._threshold_in_credits,
      is_active: true,
      price: this.selectedPrice,
    }
  }

  private get selectedPrice(): number {
    const selectedItem = this._amountItems.find((item) => item.id === this._amount_in_credits)
    if (selectedItem?.labelBrackets) {
      const priceString = String(selectedItem.labelBrackets).replace(/[^0-9.]/g, '')
      return parseFloat(priceString) || 0
    }
    return this._billingStore.autorecharge?.price || 0
  }
  private getAdditionalCreditPrice = async () => {
    try {
      const { data } = await BillingApi.getAdditionalCreditPrice()
      this._amountItems = data.map((item) => ({
        id: item.credits,
        label: numberFormat({ value: item.credits }),
        labelBrackets: numberFormat({ value: item.price, currency: 'USD' }),
        selectedLabelRight: numberFormat({ value: item.price, currency: 'USD' }),
      }))
    } catch (e) {
      logger.error(e)
    }
  }

  get disabledAutoRecharge() {
    return !this._primaryDropdownCardsStore?.activeId
  }

  onEnableAutoRecharge = async () => {
    this.clearError()

    try {
      await this._billingStore.autorecharge?.handleUpdateBillingAutoRecharge(this.params, () => {
        this.options?.onSuccessEnable?.()
        modalStore.removeModal(this._modalId)
      })
    } catch (e) {
      if (e instanceof AxiosError) {
        const firstError = e.errors.first
        if (firstError) {
          runInAction(() => {
            this._error = this.transformErrorMessage(firstError)
          })
        }
      }
    }
  }

  private transformErrorMessage = (error: string): string => {
    if (error.endsWith(' must be different.')) {
      return 'Recharge amount must be higher than your balance threshold'
    }
    return error
  }

  init = () => {
    this.clearError()
    this.getAdditionalCreditPrice()
    this._threshold_in_credits = this._billingStore.autorecharge?.threshold_in_credits || 100
    this._amount_in_credits = this._billingStore.autorecharge?.amount_in_credits || 1000
    this._primaryDropdownCardsStore.setActiveId(this._billingStore.autorecharge?.source || '')
  }

  openModal = () => {
    this.init()
    this._modalId = nanoid()

    modalStore.addModal({
      id: this._modalId,
      width: 480,
      paddingContent: '10px 24px 16px 24px',
      title: 'Configure Auto-Recharge',
      ModalActions: AutoRechargeModalAction,
      ModalContent: AutoRechargeModalContent,
      ModalContentProps: {
        autoRechargeWidgetStore: this,
      },
      zIndex: this._options?.zIndex,
      onClose: () => {
        this.options?.onFailedEnable?.()
        modalStore.removeModal(this._modalId)
      },
    })
  }

  set_threshold_in_credits = (value: typeof this._threshold_in_credits) => {
    this._threshold_in_credits = value
    this.clearError()
  }

  set_amount_in_credits = (value: typeof this._amount_in_credits) => {
    this._amount_in_credits = value
    this.clearError()
  }

  get threshold_in_credits() {
    return this._threshold_in_credits
  }

  get amountItems() {
    return this._amountItems
  }

  get amount_in_credits() {
    return this._amount_in_credits
  }

  get primaryDropdownCardsStore() {
    return this._primaryDropdownCardsStore
  }

  get options() {
    return this._options
  }

  get error() {
    return this._error
  }

  clearError = () => {
    this._error = null
  }

  get isActive() {
    return this._billingStore.autorecharge?.is_active ?? false
  }

  get autorecharge() {
    return this._billingStore.autorecharge
  }

  get thresholdInCredits() {
    return this._billingStore.autorecharge?.threshold_in_credits ?? null
  }

  get amountInCredits() {
    return this._billingStore.autorecharge?.amount_in_credits ?? null
  }

  get amount() {
    return this._billingStore.autorecharge?.amount ?? null
  }

  get price() {
    return this._billingStore.autorecharge?.price ?? null
  }

  openConfirmDisableModal = () => {
    this._confirmDisableModalId = nanoid()

    modalStore.addModal({
      id: this._confirmDisableModalId,
      type: ModalTypeList.WARNING,
      title: 'Disable Auto-Recharge?',
      zIndex: ZIndex.OVERLAY_TOP_100,
      desc: 'Disabling this feature may result in undelivered messages when your balance is low.',
      primaryAction: {
        text: 'Yes, disable',
        onAction: this.handleDisable,
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: this.closeConfirmDisableModal,
      },
    })
  }

  closeConfirmDisableModal = () => {
    modalStore.closeModal(this._confirmDisableModalId)
  }

  private updateAutoRechargeStatus = async (isActive: boolean, onSuccess?: () => void) => {
    const autorecharge = this._billingStore.autorecharge
    if (!autorecharge) return

    this.clearError()

    try {
      await autorecharge.handleUpdateBillingAutoRecharge(
        {
          threshold: autorecharge.threshold || 100,
          amount: autorecharge.amount || 1000,
          source: autorecharge.source,
          is_active: isActive,
          backup: null,
          price: autorecharge.price || 0,
        },
        onSuccess
      )
    } catch (e) {
      if (e instanceof AxiosError) {
        const firstError = e.errors.first
        if (firstError) {
          runInAction(() => {
            this._error = this.transformErrorMessage(firstError)
          })
        }
      }
    }
  }

  handleDisable = async () => {
    await this.updateAutoRechargeStatus(false, this.closeConfirmDisableModal)
  }

  handleSwitchChange = async (value: boolean) => {
    if (!value) {
      this.openConfirmDisableModal()
      return
    }

    await this.updateAutoRechargeStatus(true)
  }
}
